import { axios } from 'Fetcher'
// Module, not the 'Lib' barrel — the barrel reaches store.ts and closes a cycle.
import { getCookiesMap, getCurrentVisitUrl, getCurrentVisitUrlParams, getSearchParams } from 'Lib/AppUtils'
import { getHashedEmail } from 'Lib/Crypto'
import { Store } from 'redux'

const OAIAPI_ROUTING_PATH = '/api/collect/oaiapi'

// OpenAI's taxonomy pairs an event name with a data shape, and both are
// required. `order_created` is the one Order Created Sales and ROAS read.
// https://developers.openai.com/ads/supported-events
const ORDER_CREATED = 'order_created'
const CONTENTS = 'contents'

// Same id the Conversions API posts as `pid`. Dedup between the Pixel and the
// server event keys on pixel id + event name + event_id, so these must match.
const OPENAI_PIXEL_ID = 'PARngHrqYrURGJjWVjJra2'
const OPENAI_PIXEL_SDK_SRC = 'https://bzrcdn.openai.com/sdk/oaiq.min.js'

// Pixel only. It has no validate_only, so a staging or local purchase would land
// in the ad account as a real conversion. Same gate as Facebook/Snapchat/Twitter.
// The CAPI half is not gated: staging is __DEV__ (only my/my2.bodyfast.app are
// production hosts), and the Cloud Function sends validate_only off production,
// so gating it here would leave the server path untestable outside production.
const DISABLE_ON_DEV = true // Set to false to enable the Pixel in development

const OPPREF_COOKIE = '__oppref'
const OBREF_COOKIE = '__obref'
const OPPREF_PARAM = 'oppref'

// Ads Manager's supported landing-page template values, plus the utm set
// marketing tags alongside them:
// utm_source=chatgpt&utm_medium=cpc&utm_campaign={campaign_id}&utm_content={ad_id}
//   &campaign_id={campaign_id}&ad_group_id={ad_group_id}&ad_id={ad_id}
//   &ad_account_id={ad_account_id}
const OPENAI_AD_PARAM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content',
  'campaign_id', 'ad_group_id', 'ad_id', 'ad_account_id'
]

// Two markers, because not every ad carries the full param string. ad_account_id
// is the unambiguous one; utm_source=chatgpt catches the utm-only ads, and is
// still specific enough that no other channel sets it.
const OPENAI_AD_MARKER_KEY = 'ad_account_id'
const OPENAI_UTM_SOURCE = 'chatgpt'

// Targeting-category consent gates both halves. OpenAI asks for the same ("If
// the user revokes consent, stop sending it").
let cookieConsentGranted: boolean | null = null
let pixelLoaded = false

let store: Store | null = null

// The queue shim only. It buffers calls until the SDK drains them, and the SDK
// is not fetched until consent, so building this sends nothing.
export const initOpenAiAdsService = (newStore: Store) => {
  store = newStore

  if (window.oaiq) return

  const queue: OpenAiPixelQueue = function (...args: any[]) {
    queue.q?.push(args as unknown as IArguments)
  }
  queue.q = []
  window.oaiq = queue

  // Consent defaults to true in the SDK, so it has to be denied before init or
  // the first drain would ping with consent it was never given.
  queue('consent', false)
  queue('init', { pixelId: OPENAI_PIXEL_ID })
}

const loadOpenAiPixel = () => {
  if (pixelLoaded || !window.oaiq) return
  pixelLoaded = true

  const script = document.createElement('script')
  script.async = true
  script.src = OPENAI_PIXEL_SDK_SRC
  document.head.appendChild(script)
}

// The cookie is only there once the SDK has run on the landing page, so the URL
// param is the fallback for a first-visit conversion. It needs no ad setup:
// `oppref` is reserved in Ads Manager, and OpenAI appends it to the landing URL
// itself on click.
const getOppref = (): string | undefined =>
  getCookiesMap().get(OPPREF_COOKIE) ?? getSearchParams().get(OPPREF_PARAM) ?? undefined

const getObref = (): string | undefined => getCookiesMap().get(OBREF_COOKIE) ?? undefined

// Current visit only — the all-time store persists across sessions, so a return
// visit with no ad params would be booked to the previous visit's campaign.
// Cross-session attribution belongs to the Pixel and its __oppref cookie.
const getOpenAiAdParams = (): URLSearchParams => {
  const visitParams = getCurrentVisitUrlParams()
  const adParams = new URLSearchParams()

  OPENAI_AD_PARAM_KEYS.forEach(key => {
    const value = visitParams.get(key)

    if (value) {
      adParams.set(key, value)
    }
  })

  return adParams
}

const hasOpenAiAdAttribution = (): boolean => {
  const params = getOpenAiAdParams()

  return Boolean(params.get(OPENAI_AD_MARKER_KEY)) || params.get('utm_source') === OPENAI_UTM_SOURCE
}

// OpenAI wants an integer in the ISO 4217 minor unit: 3999 for 39.99 EUR, 1000
// for 1000 JPY, 12500 for 12.500 KWD. The exponent comes from Intl, not from
// ZERO_DECIMAL_CURRENCIES — that list is Stripe's convention for reading Stripe's
// amounts and disagrees with ISO on ISK. Same rule as amountToMinorUnits in
// firebase/functions, which the CAPI half uses.
// Rounded because 0.1 + 0.2 would otherwise give 30.000000000000004.
const toMinorUnits = (amount: number, currency: string): number => {
  // hasMonetaryPair has already rejected anything Intl would throw on.
  const { maximumFractionDigits } = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()

  return Math.round(amount * 10 ** maximumFractionDigits)
}

interface OpenAiConversionSale {
  eventId?: string
  // Major units, as saleInfo reports it.
  value?: number
  currency?: string
  // Stripe price id or PayPal plan id.
  planId?: string
}

// OpenAI only accepts an amount paired with a currency, so neither half goes on
// its own. The currency pattern is the Cloud Function's own (analytics.routes.ts),
// so the two halves of one conversion drop the same pairs. It also keeps Intl out
// of the RangeError it throws for anything but three ASCII letters.
const CURRENCY_CODE = /^[A-Za-z]{3}$/

const hasMonetaryPair = ({ value, currency }: OpenAiConversionSale): boolean =>
  Number.isFinite(value) && typeof currency === 'string' && CURRENCY_CODE.test(currency)

interface MonetaryPair { amount: number, currency: string }

const buildMonetaryPair = (sale: OpenAiConversionSale): MonetaryPair | undefined =>
  hasMonetaryPair(sale)
    ? { amount: toMinorUnits(sale.value!, sale.currency!), currency: sale.currency!.toUpperCase() }
    : undefined

// The SDK treats undefined fields as absent, in validation and in the JSON body.
interface OrderContentsData extends Partial<MonetaryPair> {
  type: typeof CONTENTS
  contents?: Array<Partial<MonetaryPair> & { id?: string, content_type: 'product', quantity: 1 }>
}

// The same body the server's order_created carries (capiUtils.ts), since the
// Pixel copy is usually the one dedup keeps. Sent without a price too: it still
// counts as a purchase, just without revenue.
const buildOrderContents = (sale: OpenAiConversionSale): OrderContentsData => {
  const price: Partial<MonetaryPair> = buildMonetaryPair(sale) ?? {}
  const hasLineItem = sale.planId !== undefined || price.amount !== undefined

  return {
    type: CONTENTS,
    ...price,
    contents: hasLineItem
      ? [{ id: sale.planId, content_type: 'product', quantity: 1, ...price }]
      : undefined
  }
}

// Routed through our own backend: the Conversions API key is server-side only.
const sendOpenAiAdsCapiConversion = async (sale: OpenAiConversionSale) => {
  const { eventId, value, currency, planId } = sale

  try {
    const oppref = getOppref()
    const obref = getObref()

    // Read only once the gates in trackOpenAiAdsConversionEvent have passed.
    // Hashed here rather than server-side, as on Twitter/Snapchat/NewsBreak, so no
    // address leaves the browser. SHAHashValue's trim + lowercase + hex SHA-256 is
    // the normalization OpenAI specifies for emails_sha256.
    const hashedEmail = await getHashedEmail(store)

    await axios.post(OAIAPI_ROUTING_PATH, {
      type: ORDER_CREATED,
      event_id: eventId,
      source_url: getCurrentVisitUrl(),
      ...(hasMonetaryPair(sale) ? { value, currency } : {}),
      ...(planId !== undefined ? { plan_id: planId } : {}),
      ...(hashedEmail !== undefined ? { hashed_email: hashedEmail } : {}),
      ...(oppref !== undefined ? { oppref } : {}),
      ...(obref !== undefined ? { obref } : {})
    })
  } catch (error: any) {
    // Never rethrow — must not break the purchase flow. Sentry only off __DEV__:
    // a local run without the functions emulator would report every event.
    logError('web_openai_eventsApiError', error?.response?.data ?? error?.message ?? error, {}, !__DEV__)
  }
}

// Carries the full body, not just `type`. Dedup is first-received-wins and the
// Pixel beats the server event (straight from the browser, versus a hop through
// our Cloud Function), so a bare Pixel event would be the one OpenAI keeps and
// the revenue would never arrive.
const measureOpenAiAdsConversion = (sale: OpenAiConversionSale) => {
  if (__DEV__ && DISABLE_ON_DEV) {
    return
  }

  window.oaiq?.('measure', ORDER_CREATED, buildOrderContents(sale), { event_id: sale.eventId })
}

export const trackOpenAiAdsConversionEvent = (eventName: AnalyticEvents | string, params: Record<string, any> = {}) => {
  // Only the subscription conversion is tracked; OpenAI Ads has no funnel events.
  if (eventName !== 'web_purchaseSuccess') {
    return
  }

  if (cookieConsentGranted !== true) {
    // Dropping is correct, but silent: OneTrust can still be resolving when
    // payment-success loads, and without this the loss reads as nothing more
    // than lower conversion volume than the other pixels.
    logError('web_openai_consentMissingError', 'Consent not granted at purchase', {
      consentState: cookieConsentGranted === null ? 'unresolved' : 'declined'
    }, false)

    return
  }

  // An event without the ad params is unmatchable, so it would be pure noise in
  // the ad account rather than an unattributed conversion.
  if (!hasOpenAiAdAttribution()) {
    return
  }

  try {
    // The Stripe/PayPal subscription id rather than a fresh UUID: a retry or a
    // reloaded payment-success page then books the same subscription once. Both
    // halves send it, which is what makes them dedup instead of double-count.
    const sale: OpenAiConversionSale = {
      eventId: params.purchaseId,
      value: params.value,
      currency: params.currency,
      planId: params.pricingId
    }

    // Rather send nothing than a conversion OpenAI cannot dedup against a retry.
    // One guard for both halves: a lone Pixel event would double-count.
    if (!sale.eventId) {
      logError('web_openai_missingEventIdError', 'No purchaseId on web_purchaseSuccess', {}, !__DEV__)
      return
    }

    // Still sent, but both halves drop the price, so it reports as 0 revenue.
    if (!hasMonetaryPair(sale)) {
      logError('web_openai_missingPriceError', 'No valid value/currency on web_purchaseSuccess', {
        value: sale.value,
        currency: sale.currency
      }, !__DEV__)
    }

    void sendOpenAiAdsCapiConversion(sale)
    measureOpenAiAdsConversion(sale)
  } catch (error) {
    logError('web_openai_trackError', error, { eventName }, true)
  }
}

export const grantOpenAiAdsConsent = () => {
  if (cookieConsentGranted === true) {
    return
  }

  cookieConsentGranted = true

  if (__DEV__ && DISABLE_ON_DEV) {
    return
  }

  try {
    loadOpenAiPixel()
    window.oaiq?.('consent', true)
  } catch (error) {
    logError('web_openai_grantConsentError', error, {}, true)
  }
}

// The SDK's own kill switch. Detaching the script would not stop it.
export const revokeOpenAiAdsConsent = () => {
  if (cookieConsentGranted === false) {
    return
  }

  cookieConsentGranted = false

  try {
    window.oaiq?.('consent', false)
  } catch (error) {
    logError('web_openai_revokeConsentError', error, {}, true)
  }
}
